import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppTopBar, formatIDR } from "@/components/AppTopBar";

export default function SupplierRefunds() {
  const { user } = useAuth();
  const [refunds, setRefunds] = useState([]);
  const [accts, setAccts] = useState([]);
  const [expenseAccts, setExpenseAccts] = useState([]);
  const [form, setForm] = useState({});   // refund_id -> {amount, account, reason}
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const isOwner = user?.role === "OWNER";

  const load = useCallback(() => {
    Promise.all([api.get("/refunds"), api.get("/accounts"), api.get("/refunds/writeoff-expense-accounts")])
      .then(([r, a, exp]) => {
        setRefunds(r.data);
        setAccts(a.data.filter((x) => x.is_active));
        setExpenseAccts(exp.data);
      })
      .catch((err) => { console.error(err); setError(formatApiErrorDetail(err.response?.data?.detail)); });
  }, []);
  useEffect(() => { if (isOwner) load(); }, [load, isOwner]);

  const set = (id, k, v) => setForm((p) => ({ ...p, [id]: { ...p[id], [k]: v } }));
  const run = async (fn) => { setError(""); setBusy(true); try { await fn(); load(); } catch (err) { console.error(err); setError(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); } };
  const receive = (r) => run(async () => {
    const s = form[r.refund_id] || {};
    await api.post(`/refunds/${r.refund_id}/receive`, { amount: parseFloat(s.amount || "0"), destination_account_id: s.account, idempotency_key: crypto.randomUUID(), note: s.reason });
    set(r.refund_id, "amount", "");
  });
  const writeoff = (r) => run(async () => {
    const s = form[r.refund_id] || {};
    await api.post(`/refunds/${r.refund_id}/writeoff`, {
      amount: parseFloat(s.amount || "0"),
      reason: s.reason || "",
      expense_account_id: s.expenseAccount || null,
    });
    set(r.refund_id, "amount", "");
  });

  if (user && !isOwner) {
    return (<div className="app-shell"><AppTopBar active="accounts" /><main className="content"><div className="alert-error" data-testid="access-denied">Akses ditolak.</div></main></div>);
  }

  return (
    <div className="app-shell">
      <AppTopBar active="accounts" />
      <main className="content">
        {error && <div className="alert-error" data-testid="error-banner">{error}</div>}
        <Link to="/accounts" className="back-link" data-testid="to-accounts">← Master Account</Link>

        <section className="panel" data-testid="refunds-panel">
          <h2 className="panel-title">Supplier Refund ({refunds.length})</h2>
          <p className="hint">Refund muncul otomatis saat Purchase quantity dibatalkan (CANCEL_REMAINING / WRONG_SKU_CANCEL). Ongkir tidak termasuk.</p>
          <div className="table-scroll">
            <table className="data-table" data-testid="refunds-table">
              <thead><tr><th>Purchase</th><th>Expected</th><th>Received</th><th>Written Off</th><th>Outstanding</th><th>Status</th><th>Aksi</th></tr></thead>
              <tbody>
                {refunds.length === 0 && <tr><td colSpan={7} className="empty-cell">Belum ada refund.</td></tr>}
                {refunds.map((r) => {
                  const s = form[r.refund_id] || {};
                  const open = Number(r.outstanding_amount) > 0;
                  return (
                    <tr key={r.refund_id} data-testid={`refund-row-${r.purchase_no}`}>
                      <td className="mono">{r.purchase_no}</td>
                      <td className="mono">{formatIDR(r.expected_amount)}</td>
                      <td className="mono" data-testid={`received-${r.purchase_no}`}>{formatIDR(r.received_amount)}</td>
                      <td className="mono">{formatIDR(r.written_off_amount)}</td>
                      <td className="mono" data-testid={`outstanding-${r.purchase_no}`}>{formatIDR(r.outstanding_amount)}</td>
                      <td><span className={`status-pill ${r.status === "RECEIVED" ? "on" : "off"}`} data-testid={`status-${r.purchase_no}`}>{r.status}</span></td>
                      <td>
                        {open && (
                          <div style={{ display: "flex", gap: 4, flexWrap: "wrap", alignItems: "center" }}>
                            <input className="field-input narrow" type="number" placeholder="Rp" value={s.amount || ""} onChange={(e) => set(r.refund_id, "amount", e.target.value)} data-testid={`refund-amount-${r.purchase_no}`} />
                            <select className="field-input narrow" value={s.account || ""} onChange={(e) => set(r.refund_id, "account", e.target.value)} data-testid={`refund-account-${r.purchase_no}`}>
                              <option value="">akun</option>{accts.map((a) => <option key={a.account_id} value={a.account_id}>{a.account_name}</option>)}
                            </select>
                            <input className="field-input narrow" placeholder="alasan (write-off)" value={s.reason || ""} onChange={(e) => set(r.refund_id, "reason", e.target.value)} data-testid={`refund-reason-${r.purchase_no}`} />
                            <select className="field-input narrow" value={s.expenseAccount || ""} onChange={(e) => set(r.refund_id, "expenseAccount", e.target.value)} title="Pilih akun biaya yang sesuai untuk penghapusan piutang" data-testid={`refund-expense-account-${r.purchase_no}`}>
                              <option value="">COA biaya write-off</option>
                              {expenseAccts.map((a) => <option key={a.account_id} value={a.account_id}>{a.account_code} — {a.account_name}</option>)}
                            </select>
                            <button className="btn-mini" onClick={() => receive(r)} disabled={busy || !s.amount || !s.account} data-testid={`receive-btn-${r.purchase_no}`}>Terima</button>
                            <button className="btn-mini" onClick={() => writeoff(r)} disabled={busy || !s.amount || !s.reason || !s.expenseAccount} data-testid={`writeoff-btn-${r.purchase_no}`}>Write Off</button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  );
}
